/**
 * RowActions —— 合作内容「行操作」列的内容（删除 / 复制 / 字段应用至整单）。
 *
 * 【交互来源】复用 `CooperationFormList/index.tsx` 原设计：应用至整单走
 * `DropdownListApplyToTheWholeOrder` 弹层。
 *
 * 【为何列宽常量与它同处】`ACTION_COLUMN_WIDTH` 的取值依据是**实测这个组件的内容宽**
 * （见下），改了按钮/图标就该重算列宽 —— 两者分开必然漂移，故一起放在本文件。
 *
 * 【为何 `hasRowContent` 也放这里】它是**本组件删除按钮是否需要二次确认**的唯一判据，
 * 与「删除」这一交互同生共死；放到别处会让「什么时候要确认」与「按钮怎么画」分家。
 */
import React, { useState } from 'react';
import { Button, Dropdown, Menu, Popconfirm } from '@bedrock/components';
import { CopyNewAdd, ChevronDown, Copy2 } from '@bedrock/icons-react';
import DropdownListApplyToTheWholeOrder from './DropdownListApplyToTheWholeOrder';
import { isEmptyFieldValue } from './propertyValidation';
import { cooperationValueToFormValue } from './propertyPipe';
import type { CoverMode } from './applyToWholeOrder';

/**
 * 行操作列宽（`fixed:'right'`，横向滚动时恒占右侧视野）。
 *
 * 取值 = 内容宽 + cell 左右 padding(13.2×2)。内容为「删除」按钮 + `gap-1` + 无文字的
 * 「更多」箭头按钮（末尾 `DropdownListApplyToTheWholeOrder` 的触发占位 span 宽 0）。
 *
 * ⚠️ 本列越窄，考核指标列横向滚动后的可见区越大，故按「刚好容纳」取值，不留余量：
 * 改按钮文案 / 图标尺寸 / gap 后必须重算。@bedrock 表格列宽 **rem : px = 1 : 1**（92 即 92px），
 * 收紧过头会把「删除」二字挤换行。
 */
export const ACTION_COLUMN_WIDTH = 92;

/** 构成「行有内容」的子数组字段：任一非空即视为该行已有内容 */
const ROW_CONTENT_ARRAY_KEYS = ['kpi_values', 'necessary_rights', 'expect_rights'];
/** 构成「行有内容」的行级标量字段（备注 / 是否需包含 AI） */
const ROW_CONTENT_SCALAR_KEYS = ['remark', 'need_ai'];

/**
 * 该行是否已有内容（属性值 / 指标 / 权益 / 备注 / 是否需包含AI 任一非空）。
 *
 * 【用途】决定「删除」是否需要二次确认：有内容的行弹气泡确认，空行直接删除 ——
 * 空行删掉无损失，弹确认只是徒增一次点击。
 */
export const hasRowContent = (row: Record<string, any>): boolean => {
  if (ROW_CONTENT_ARRAY_KEYS.some((k) => (row?.[k] || []).length > 0)) return true;
  if (ROW_CONTENT_SCALAR_KEYS.some((k) => !isEmptyFieldValue(row?.[k]))) return true;
  const formMap = cooperationValueToFormValue(row?.property_values);
  return Object.keys(formMap).some((k) => !isEmptyFieldValue(formMap[k]));
};

/** 删除后二次确认文案（气泡确认框） */
const DELETE_CONFIRM_TITLE = '删除后该条合作内容已填写的信息会一并清除，确定删除吗？';

export function RowActions({ id, row, schema, hideOptions, isSingle, onCopy, onDelete, onApplyToWholeOrder }: {
  id: number;
  /** 所属行数据：用于判定该行是否已有内容（决定删除是否需要二次确认） */
  row: Record<string, any>;
  schema: any;
  /** 「字段应用至整单」弹层中不提供为可应用字段的选项（对齐折叠面板的 hideOptions） */
  hideOptions: { name: string; hide: boolean }[];
  /** 仅剩一行时禁止删除（对齐折叠面板：删除按钮 disabled={dataList.length <= 1}） */
  isSingle: boolean;
  onCopy: (id: number) => void; onDelete: (id: number) => void;
  onApplyToWholeOrder: (id: number, applyConfig: { coverMode: CoverMode; coverKeys: string[] }) => void;
}) {
  // 布局：删除 最左；右侧「更多」下拉 → 复制(第一) / 应用至整单(第二)。
  // 应用至整单打开受控 popover（DropdownListApplyToTheWholeOrder hideTrigger），规避 Dropdown+Popover 嵌套卸载冲突。
  // 「更多」仅保留下拉箭头图标、不带文字：操作列是 fixed:'right' 的钉住列，横向滚动时恒定占据右侧视野，
  // 文字会让该列明显变宽、挤压考核指标列的可见区（用户反馈）。
  const [ddOpen, setDdOpen] = useState(false);
  const [applyOpen, setApplyOpen] = useState(false);
  const onMenuClick = ({ key }: { key: string }) => {
    if (key === 'copy') { onCopy(id); setDdOpen(false); }
    else if (key === 'apply') { setDdOpen(false); setApplyOpen(true); }
  };
  // 样式与「期望权益」子表操作列的删除按钮保持一致：
  // 不带图标、type="text"（text-subtle 的 hover 底色与 danger 叠加会发红，观感不符）。
  const deleteButton = (
    <Button size="small" type="text" danger disabled={isSingle} onClick={() => onDelete(id)}>删除</Button>
  );
  return (
    <div className="flex items-center gap-1">
      {hasRowContent(row) ? (
        // 有内容 → 气泡二次确认；空行直接删除
        <Popconfirm title={DELETE_CONFIRM_TITLE} onOk={() => onDelete(id)}>
          {deleteButton}
        </Popconfirm>
      ) : deleteButton}
      <Dropdown trigger="click" open={ddOpen} onOpenChange={setDdOpen} overlay={(
        <Menu onClick={onMenuClick}>
          <Menu.Item key="copy" icon={<CopyNewAdd />}>复制</Menu.Item>
          <Menu.Item key="apply" icon={<Copy2 />}>字段应用至整单</Menu.Item>
        </Menu>
      )}>
        <Button type="text-subtle" size="xSmall" suffixIcon={<ChevronDown />} aria-label="更多操作" />
      </Dropdown>
      <DropdownListApplyToTheWholeOrder schema={schema} hideOptions={hideOptions} hideTrigger open={applyOpen} onOpenChange={setApplyOpen} onOk={(applyConfig) => { onApplyToWholeOrder(id, applyConfig); setApplyOpen(false); }} />
    </div>
  );
}
